import React from "react";
import DataCorrectionApprover from "./DataCorrectionApprover";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { PERMISSIONS } from "../utils/permissions";
import StatusBadge from "./StatusBadge";
import { getCorrectionFieldKeys, fetchIndustryFields } from "../api";

export function DataCorrectionTable({
  requests,
  loading,
  isRevertedTab,
  canApprove,
  onDownload,
  onApprove,
  onReject,
  onViewLogs,
}) {
  const { theme } = useTheme();
  const [detailsModalOpen, setDetailsModalOpen] = React.useState(false);
  const [selectedRequest, setSelectedRequest] = React.useState(null);
  const [detailMeta, setDetailMeta] = React.useState({
    correctionKeys: [],
    industryFields: [],
  });

  const handleViewDetails = async (req) => {
    setSelectedRequest(req);
    setDetailsModalOpen(true);
    setDetailMeta({ correctionKeys: [], industryFields: [] });
    try {
      const keysRes = await getCorrectionFieldKeys();
      const correctionKeys = keysRes.data.keys || [];
      let industryFields = [];
      if (req.is_enrichment && req.industry) {
        try {
          const fRes = await fetchIndustryFields(req.industry);
          industryFields = fRes.data.fields || [];
        } catch (_) {
          industryFields = [];
        }
      }
      setDetailMeta({ correctionKeys, industryFields });
    } catch (_) {
      setDetailMeta({ correctionKeys: [], industryFields: [] });
    }
  };

  return (
    <>
      <table
        width="100%"
        cellPadding={10}
        style={{ borderCollapse: "collapse" }}
      >
        <thead>
          <tr style={{ background: "#f3f4f6" }}>
            <th align="left">User</th>
            <th align="left">File</th>
            <th align="left">Status</th>
            <th align="left">
              {isRevertedTab ? "Reverted At" : "Uploaded"}
            </th>
            <th align="left">Reverted</th>
            <th align="left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td
                colSpan={6}
                style={{ textAlign: "center", padding: "15px" }}
              >
                Loading...
              </td>
            </tr>
          )}
          {!loading && requests.length === 0 && (
            <tr>
              <td
                colSpan={6}
                style={{ textAlign: "center", padding: "15px" }}
              >
                No requests found.
              </td>
            </tr>
          )}
          {!loading &&
            requests.map((r) => (
              <tr
                key={r.request_id}
                style={{ borderTop: "1px solid #eee" }}
              >
                <td>{r.user_id}</td>
                <td>
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start" }}>
                    <span>{r.file_name}</span>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: "6px",
                        marginTop: "4px",
                      }}
                    >
                      {r.is_enrichment ? (
                        <span
                          style={{
                            fontSize: "10px",
                            padding: "1px 5px",
                            borderRadius: "4px",
                            background: "#e0f2fe",
                            color: "#0369a1",
                            border: "1px solid #bae6fd",
                            fontWeight: 600,
                          }}
                        >
                          ENRICHMENT
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: "10px",
                            padding: "1px 5px",
                            borderRadius: "4px",
                            background: "#f3f4f6",
                            color: "#4b5563",
                            border: "1px solid #e5e7eb",
                            fontWeight: 600,
                          }}
                        >
                          STANDARD
                        </span>
                      )}
                      <span
                        style={{
                          fontSize: "11px",
                          color: "#64748b",
                          fontWeight: 500,
                        }}
                      >
                        Industry:{" "}
                        {r.is_enrichment && r.industry
                          ? r.industry
                          : "null"}
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <StatusBadge status={r.status} />
                    {r.approver_id && (
                      <span style={{ fontSize: "11px", color: "#6b7280" }}>
                        By: {r.approver_id}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  {isRevertedTab && r.reverted_at
                    ? new Date(r.reverted_at).toLocaleString()
                    : new Date(r.uploaded_at).toLocaleString()}
                </td>
                <td>
                  {r.reverted ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <span
                        style={{
                          width: "fit-content",
                          fontSize: "11px",
                          padding: "3px 8px",
                          borderRadius: "4px",
                          background: "#fff7ed",
                          color: "#c05621",
                          border: "1px solid #f97316",
                          fontWeight: 500,
                        }}
                      >
                        REVERTED
                      </span>
                      {r.reverted_by && (
                        <span style={{ fontSize: "11px", color: "#6b7280" }}>
                          By: {r.reverted_by}
                        </span>
                      )}
                    </div>
                  ) : (
                    "-"
                  )}
                </td>
                <td style={{ display: "flex", gap: "6px" }}>
                  <button
                    onClick={() => onDownload(r.request_id)}
                    style={{
                      padding: "6px 10px",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      background: "#f9fafb",
                      cursor: "pointer",
                    }}
                    title="Download CSV"
                  >
                    ⬇
                  </button>

                  {r.field_mappings &&
                    Object.keys(r.field_mappings).length > 0 && (
                    <button
                      onClick={() => handleViewDetails(r)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        background: "#f0f9ff",
                        color: "#0284c7",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 500
                      }}
                      title="View field mappings"
                    >
                      Mappings
                    </button>
                  )}

                  {/* Approve / Reject only in PENDING tab and only for approvers */}
                  {canApprove && r.status === "PENDING" && (
                    <>
                      <button
                        onClick={() => onApprove(r)}
                        style={{
                          padding: "6px 10px",
                          borderRadius: "6px",
                          border: "none",
                          background: "#2ecc71",
                          color: "#fff",
                          cursor: "pointer",
                        }}
                        title="Approve"
                      >
                        ✔
                      </button>
                      <button
                        onClick={() => onReject(r)}
                        style={{
                          padding: "6px 10px",
                          borderRadius: "6px",
                          border: "none",
                          background: "#e74c3c",
                          color: "#fff",
                          cursor: "pointer",
                        }}
                        title="Reject"
                      >
                        ✖
                      </button>
                    </>
                  )}

                  {onViewLogs && r.status === "APPROVED" && (
                    <button
                      onClick={() => onViewLogs(r.request_id)}
                      style={{
                        padding: "6px 16px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        background: "#eef2ff",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      Logs
                    </button>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {detailsModalOpen && selectedRequest && (() => {
        const standardSet = new Set(detailMeta.correctionKeys);
        const industrySet = new Set(detailMeta.industryFields);
        const mappingTargets = [
          ...new Set(Object.values(selectedRequest.field_mappings || {})),
        ].filter(Boolean);
        const newCustomTargets = selectedRequest.is_enrichment
          ? mappingTargets.filter(
              (v) => !standardSet.has(v) && !industrySet.has(v)
            )
          : [];

        return (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: theme.cardBackground,
            padding: '24px',
            borderRadius: '8px',
            width: '560px',
            maxWidth: '90%',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            border: `1px solid ${theme.border}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: theme.text }}>Request details</h3>
              <button
                onClick={() => setDetailsModalOpen(false)}
                style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: theme.textMuted }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '15px', color: theme.text, fontSize: '14px' }}>
              <div><strong>Industry:</strong>{" "}
                {selectedRequest.is_enrichment && selectedRequest.industry
                  ? selectedRequest.industry
                  : "— (standard correction)"}
              </div>
              <div><strong>File:</strong> {selectedRequest.file_name}</div>
            </div>

            {selectedRequest.is_enrichment && selectedRequest.industry && (
              <div style={{ marginBottom: '12px', fontSize: '12px', color: theme.textSecondary }}>
                <strong style={{ color: theme.text }}>Industry fields (existing):</strong>{" "}
                {detailMeta.industryFields.length
                  ? detailMeta.industryFields.join(", ")
                  : "None loaded or new industry."}
              </div>
            )}

            {selectedRequest.is_enrichment && newCustomTargets.length > 0 && (
              <div style={{
                marginBottom: '12px',
                padding: '10px 12px',
                borderRadius: '6px',
                background: '#fffbeb',
                border: '1px solid #fcd34d',
                fontSize: '12px',
                color: '#92400e'
              }}>
                <strong>New custom field targets in this upload:</strong>{" "}
                {newCustomTargets.join(", ")}
              </div>
            )}

            <h4 style={{ marginBottom: '10px', fontSize: '14px', color: theme.textSecondary }}>Field mappings (CSV → target)</h4>
            <div style={{ overflowY: 'auto', border: `1px solid ${theme.border}`, borderRadius: '4px' }}>
              <table width="100%" cellPadding={8} style={{ borderCollapse: 'collapse' }}>
                <thead style={{ background: theme.tableHeader, borderBottom: `1px solid ${theme.border}` }}>
                  <tr>
                    <th align="left" style={{ fontSize: '12px', color: theme.textSecondary }}>CSV column</th>
                    <th align="left" style={{ fontSize: '12px', color: theme.textSecondary }}>Mapped to</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(selectedRequest.field_mappings || {}).map(([csvKey, targetKey]) => (
                    <tr key={csvKey} style={{ borderBottom: `1px solid ${theme.borderLight}` }}>
                      <td style={{ fontSize: '13px', color: theme.text }}>{csvKey}</td>
                      <td style={{ fontSize: '13px', fontWeight: 500, color: theme.primary }}>{targetKey}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                onClick={() => setDetailsModalOpen(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: `1px solid ${theme.border}`,
                  background: theme.cardBackground,
                  cursor: 'pointer',
                  color: theme.text
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </>
  );
}

export default function DataCorrectionPage({ onViewLogs }) {
  const { hasPermission } = useAuth();

  const canApprove = hasPermission(PERMISSIONS.DATA_CORRECTION_APPROVER);
  const canUpload = hasPermission(PERMISSIONS.DATA_CORRECTION_UPLOADER);

  return <DataCorrectionApprover onViewLogs={onViewLogs} />;
}